import {
  Aperture,
  Brush,
  Focus,
  ImageUpscale,
  Layers3,
  Lightbulb,
  ScanFace,
  Sparkles,
  WandSparkles,
  Waves
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { estimateJobCredits } from '../../shared/billing';
import { toolFaqSupplements } from './toolFaqSupplements';
import { expandedTools } from './expandedTools';
import { toolLongDescriptions } from './toolLongDescriptions';

export type ToolSlug =
  | 'enhance'
  | 'upscale'
  | 'restore'
  | 'face-restore'
  | 'remove-background'
  | 'deblur'
  | 'denoise'
  | 'sharpen'
  | 'relight'
  | 'product-photo'
  | 'vectorize'
  | 'object-remover'
  | 'text-remover'
  | 'image-extender'
  | 'background-changer'
  | 'photo-colorizer'
  | 'image-editor'
  | 'compress'
  | 'resize'
  | 'crop'
  | 'convert'
  | 'social-resize'
  | 'color-picker'
  | 'palette-generator'
  | 'metadata-remover';

export type ToolOption = {
  id: string;
  label: string;
  type: 'select' | 'toggle' | 'range' | 'text';
  defaultValue: string | number | boolean;
  choices?: { label: string; value: string }[];
  min?: number;
  max?: number;
  step?: number;
  help?: string;
};

export type ToolDefinition = {
  slug: ToolSlug;
  name: string;
  shortName: string;
  eyebrow: string;
  description: string;
  longDescription: string;
  seoTitle: string;
  seoDescription: string;
  searchTerms: string[];
  icon: LucideIcon;
  creditCost: number;
  processingTime: string;
  formats: string[];
  recommendedFor: string[];
  options: ToolOption[];
  badge?: string;
  mode?: 'ai' | 'free';
  category?: string;
  benefits: { title: string; description: string }[];
  howItWorks: { title: string; description: string }[];
  tips: string[];
  faqs: { question: string; answer: string }[];
};

const baseTools: ToolDefinition[] = [
  {
    slug: 'enhance',
    name: 'AI Image Enhancer',
    shortName: 'Enhance',
    eyebrow: 'Improve image quality online',
    description: 'Improve clarity, texture and tonal balance with an adjustable professional detail model and optional smart upscaling while keeping the image natural.',
    longDescription:
      'Enhance low-quality photos, make pictures clearer and improve compressed images with one professional detail-recovery model. Adjust the detail intensity with a precise slider, keep the original dimensions by default, or optionally add 2×, 4× or 8× smart upscaling when you need a larger result.',
    seoTitle: 'AI Image Enhancer Online - Improve Image Quality',
    seoDescription: 'Improve image quality and make pictures clearer online with controlled AI enhancement for natural detail, contrast and texture.',
    searchTerms: ['enhance image quality', 'photo enhancer', 'improve image quality', 'make picture clearer', 'AI photo enhancer'],
    icon: Sparkles,
    creditCost: 4,
    processingTime: '10–40 sec',
    formats: ['JPG', 'PNG', 'WebP', 'AVIF'],
    recommendedFor: ['Social posts', 'Portraits', 'Low-quality exports'],
    options: [
      {
        id: 'detailStrength',
        label: 'Detail intensity',
        type: 'range',
        defaultValue: 70,
        min: 25,
        max: 100,
        step: 1,
        help: 'Lower values protect natural texture; higher values recover stronger visible detail.'
      },
      {
        id: 'scale',
        label: 'Optional upscale',
        type: 'select',
        defaultValue: '1',
        choices: [
          { label: 'No upscale · original size', value: '1' },
          { label: '2× upscale', value: '2' },
          { label: '4× upscale', value: '4' },
          { label: '8× upscale', value: '8' }
        ],
        help: 'Keep the original dimensions or add more pixels when the final use requires them.'
      }
    ],
    badge: 'Most popular',
    benefits: [
      { title: 'Adjustable professional detail', description: 'Use one high-quality enhancement model and control its reconstruction strength with a precise intensity slider.' },
      { title: 'Upscaling only when needed', description: 'Keep the original image dimensions by default, or choose 2×, 4× or 8× output when your project genuinely needs more pixels.' },
      { title: 'Useful for everyday and client files', description: 'A practical first step for phone photos, social media exports, creative work and small client images.' }
    ],
    howItWorks: [
      { title: 'Upload the best original', description: 'Choose the least-compressed version available in JPG, PNG, WebP or AVIF.' },
      { title: 'Set detail and optional size', description: 'Adjust the detail-intensity slider, then leave upscaling off or select 2×, 4× or 8× output for larger delivery.' },
      { title: 'Compare before downloading', description: 'Inspect faces, text, hair and repeating texture with the before-and-after slider before saving the result.' }
    ],
    tips: [
      'Start around 60–70% for portraits and already-clean images.',
      'Move the slider higher when the source is visibly soft or compressed, then inspect faces and small text.',
      'Leave upscaling off unless you need larger pixel dimensions for print, cropping or marketplace zoom.',
      'Keep the original file for reference and archival use.'
    ],
    faqs: [
      { question: 'Can AI really improve a low-quality image?', answer: 'It can improve clarity, reduce visible artifacts and strengthen useful detail, but it cannot recover every fact that was never captured. Results depend heavily on the source image.' },
      { question: 'Will the enhancer change faces?', answer: 'Higher detail intensity can reconstruct more aggressively, so always compare eyes, teeth, hair and jewelry before professional or client use. Lower the slider when the source already contains clear facial detail.' },
      { question: 'Can I enhance without upscaling?', answer: 'Yes. Original-size output is the default. You can optionally choose 2× or 4× output on Free and Starter accounts, while Creator and Studio accounts can also select 8× when the final pixel count is supported.' }
    ]
  },
  {
    slug: 'upscale',
    name: 'AI Image Upscaler',
    shortName: 'Upscale',
    eyebrow: 'Upscale images 2×, 4× or 8×',
    description: 'Increase resolution for print, e-commerce and high-resolution creative work.',
    longDescription:
      'Increase image resolution online by upscaling to 2×, 4× or 8× the original width and height. Nexhance AI applies a controlled enlargement workflow that protects edges, faces, typography and overall composition while limiting visible quality loss.',
    seoTitle: 'AI Image Upscaler Online - Increase Image Resolution',
    seoDescription: 'Increase image resolution online with controlled 2x, 4x or 8x AI upscaling for print, product photos, artwork and design.',
    searchTerms: ['image upscaler', 'upscale image', 'increase image resolution', 'upscale image 4k', 'AI upscaler'],
    icon: ImageUpscale,
    creditCost: 3,
    processingTime: '10–35 sec',
    formats: ['JPG', 'PNG', 'WebP', 'AVIF'],
    recommendedFor: ['Print', 'AI artwork', 'Small downloads'],
    options: [
      {
        id: 'scale',
        label: 'Upscale amount',
        type: 'select',
        defaultValue: '2',
        choices: [
          { label: '2×', value: '2' },
          { label: '4×', value: '4' },
          { label: '8×', value: '8' }
        ],
        help: 'Use the smallest size that reaches your delivery requirement.'
      }
    ],
    benefits: [
      { title: 'Real pixel enlargement', description: '2×, 4× and 8× options increase actual image dimensions for larger delivery and print use.' },
      { title: 'Automatic quality handling', description: 'Nexhance AI chooses a controlled enlargement path without exposing confusing technical choices.' },
      { title: 'Clear credit estimate', description: 'The studio shows the credit cost from the selected multiplier before processing.' }
    ],
    howItWorks: [
      { title: 'Check your required size', description: 'Know the pixel dimensions needed for the website, marketplace, crop or print.' },
      { title: 'Choose 2×, 4× or 8×', description: 'Use the smallest multiplier that reaches the target to reduce cost, waiting time and unnecessary reconstruction.' },
      { title: 'Inspect fine structures', description: 'Review faces, lettering, architecture and repeating patterns before final use.' }
    ],
    tips: [
      '2× is usually enough for web, presentations and moderate print enlargement.',
      '4× is useful for small sources, cropping and larger print dimensions.',
      '8× is best reserved for very small images or aggressive enlargement needs.',
      'Review logos, line art and typography carefully after enlargement.'
    ],
    faqs: [
      { question: 'What is the difference between 2×, 4× and 8× upscaling?', answer: '2× doubles width and height, creating four times the pixels. 4× quadruples width and height, creating sixteen times the pixels. 8× is a much larger reconstruction step that should be used only when the source is genuinely too small.' },
      { question: 'Does upscaling make every image sharp?', answer: 'No. Upscaling increases dimensions and estimates missing detail. Severe blur, noise or damage should be corrected with the matching restoration tool first.' },
      { question: 'Which option is best for artwork?', answer: 'Use 2× when it reaches the required size, 4× when the source is small or will be cropped, and 8× only when you truly need a much larger file. Always inspect linework and typography.' }
    ]
  },
  {
    slug: 'restore',
    name: 'Old Photo Restoration',
    shortName: 'Restore',
    eyebrow: 'Restore old photos online',
    description: 'Fix scratches, fading, weak contrast and lost detail in old or damaged photographs.',
    longDescription:
      'Restore scanned old photos with separate controls for scratches, faded colors and resolution. The workflow is designed for family archives and historical photographs where preserving facial identity and the original character matters more than dramatic reconstruction.',
    seoTitle: 'AI Old Photo Restoration Online - Repair Damaged Photos',
    seoDescription: 'Restore old photos online with AI. Repair scratches, dust, fading, weak color and lost detail in scanned family photographs.',
    searchTerms: ['old photo restoration', 'restore old photos', 'repair damaged photo', 'remove scratches from photo', 'fix faded photos'],
    icon: Brush,
    creditCost: 6,
    processingTime: '20–50 sec',
    formats: ['JPG', 'PNG', 'WebP'],
    recommendedFor: ['Family archives', 'Scanned photos', 'Faded prints'],
    options: [
      { id: 'removeScratches', label: 'Remove scratches and dust', type: 'toggle', defaultValue: true },
      { id: 'fixColors', label: 'Repair faded colors', type: 'toggle', defaultValue: true },
      { id: 'enhanceResolution', label: 'Enhance resolution', type: 'toggle', defaultValue: true }
    ],
    badge: 'Best differentiator',
    benefits: [
      { title: 'Separate restoration controls', description: 'Use scratch repair, color recovery and resolution enhancement only where needed.' },
      { title: 'Archive-friendly workflow', description: 'Designed around careful comparison rather than turning every old photograph into a modern portrait.' },
      { title: 'Face-aware recovery', description: 'Can improve visibility of damaged faces while keeping the source image available for reference.' }
    ],
    howItWorks: [
      { title: 'Create a clean scan', description: 'Capture the full print with even light and save an untouched archival master.' },
      { title: 'Choose repair options', description: 'Enable scratches, faded color and resolution only when those problems are visible.' },
      { title: 'Verify identity and history', description: 'Compare the result with the original and known photographs before sharing or printing.' }
    ],
    tips: [
      'Scan the photo at the highest practical quality before restoration.',
      'Keep black-and-white restoration separate from any interpretive colorization.',
      'Treat newly generated facial detail as reconstruction, not historical evidence.',
      'Save both the untouched scan and restored copy.'
    ],
    faqs: [
      { question: 'Can restoration remove deep scratches and folds?', answer: 'It can reduce many visible defects, but damage that covers important facial or object detail requires the workflow to estimate what was missing. Always compare the result with the original.' },
      { question: 'Will old photo restoration change the person?', answer: 'Strong reconstruction can alter small facial details. Use conservative settings and compare with other known photos when identity is important.' },
      { question: 'Can it colorize black-and-white photos?', answer: 'The current restoration workflow repairs faded color in color photographs. Any full colorization of a monochrome photo would be interpretive and should be labeled accordingly.' }
    ]
  },
  {
    slug: 'face-restore',
    name: 'Face Restoration',
    shortName: 'Face restore',
    eyebrow: 'Recover facial detail',
    description: 'Recover facial detail in old, compressed or low-resolution portraits.',
    longDescription:
      'Restore faces in old portraits, compressed profile pictures and low-resolution photographs with adjustable identity fidelity. Higher fidelity protects the visible structure; lower fidelity allows stronger reconstruction when damage is severe.',
    seoTitle: 'AI Face Restoration Online - Recover Portrait Detail',
    seoDescription: 'Restore blurry, compressed or damaged faces in portraits with adjustable identity fidelity and natural detail recovery.',
    searchTerms: ['face restoration', 'restore face photo', 'fix blurry face', 'enhance portrait face', 'AI face enhancer'],
    icon: ScanFace,
    creditCost: 3,
    processingTime: '8–20 sec',
    formats: ['JPG', 'PNG', 'WebP'],
    recommendedFor: ['Portraits', 'Profile photos', 'Old faces'],
    options: [
      {
        id: 'fidelity',
        label: 'Identity fidelity',
        type: 'range',
        defaultValue: 0.92,
        min: 0.8,
        max: 1,
        step: 0.05,
        help: 'Increase for stronger identity preservation.'
      },
      { id: 'onlyCenterFace', label: 'Prioritize the center face', type: 'toggle', defaultValue: false }
    ],
    benefits: [
      { title: 'Adjustable identity protection', description: 'Control how closely reconstruction should follow the visible facial structure.' },
      { title: 'Portrait-focused processing', description: 'Targets facial detail instead of applying strong texture changes to the entire image.' },
      { title: 'Useful for compressed faces', description: 'Improves faces affected by small downloads, messaging compression and old scans.' }
    ],
    howItWorks: [
      { title: 'Upload a portrait', description: 'Use an image where at least the main facial outline and features are visible.' },
      { title: 'Set fidelity', description: 'Increase fidelity for identity preservation or lower it cautiously for stronger reconstruction.' },
      { title: 'Review every face', description: 'Check eyes, mouth, hairline, expression and accessories before accepting the result.' }
    ],
    tips: [
      'Use higher fidelity when the person must remain recognizable.',
      'Prioritize the center face when background faces should not be reconstructed strongly.',
      'Do not use AI face restoration for identity verification or forensic evidence.',
      'For scratched prints, run old photo restoration before face restoration.'
    ],
    faqs: [
      { question: 'What does identity fidelity mean?', answer: 'It controls the balance between preserving the visible original face and allowing stronger reconstruction of missing detail. Higher values are more conservative.' },
      { question: 'Can it restore a face that is completely missing?', answer: 'No reliable system can recover a face that contains no usable visual information. The workflow may invent a plausible face, but it would not be a factual restoration.' },
      { question: 'Can I process group photos?', answer: 'Yes, but inspect every face carefully. The center-face option can focus processing when one subject is more important than the others.' }
    ]
  },
  {
    slug: 'remove-background',
    name: 'Background Remover',
    shortName: 'Remove background',
    eyebrow: 'Remove image backgrounds',
    description: 'Remove backgrounds from products, portraits and marketing assets.',
    longDescription:
      'Remove image backgrounds online and create clean transparent PNG cutouts for product listings, thumbnails and design work. Choose transparent, white or black output while preserving fine subject edges as naturally as possible.',
    seoTitle: 'AI Background Remover Online - Transparent PNG Cutouts',
    seoDescription: 'Remove image backgrounds online with AI. Create transparent PNG, white or black backgrounds for products, portraits and design assets.',
    searchTerms: ['background remover', 'remove background from image', 'transparent background', 'remove photo background', 'product background removal'],
    icon: Layers3,
    creditCost: 3,
    processingTime: '8–18 sec',
    formats: ['JPG', 'PNG', 'WebP', 'AVIF'],
    recommendedFor: ['E-commerce', 'Thumbnails', 'Design assets'],
    options: [
      {
        id: 'output',
        label: 'Output background',
        type: 'select',
        defaultValue: 'transparent',
        choices: [
          { label: 'Transparent PNG', value: 'transparent' },
          { label: 'White background', value: 'white' },
          { label: 'Black background', value: 'black' }
        ]
      }
    ],
    benefits: [
      { title: 'Multiple ready-to-use outputs', description: 'Create transparent assets or place the cutout on a clean white or black background.' },
      { title: 'Commercial workflow focus', description: 'Built for product images, marketing layouts, thumbnails and reusable design assets.' },
      { title: 'Edge-first quality check', description: 'The comparison workflow makes it easy to inspect hair, fur, straps and reflective edges.' }
    ],
    howItWorks: [
      { title: 'Choose a clear source', description: 'Images with visible subjects and clean edge contrast generally produce the strongest masks.' },
      { title: 'Select the output', description: 'Use transparency for design work or a solid background for direct listing use.' },
      { title: 'Inspect difficult edges', description: 'Check hair, glass, lace, shadows and fine product components before download.' }
    ],
    tips: [
      'Use transparent PNG for flexible design placement.',
      'Preview the cutout on both light and dark backgrounds to find halos.',
      'Keep useful contact shadows when the subject should appear grounded.',
      'Avoid background replacement that changes or misrepresents a product.'
    ],
    faqs: [
      { question: 'Does a transparent result download as PNG?', answer: 'Yes. Transparency requires an alpha-capable format, so the transparent workflow returns a PNG result.' },
      { question: 'Why are hair and glass difficult to cut out?', answer: 'They contain partially transparent or very fine edges where foreground and background colors mix. A clean, high-contrast source improves the result.' },
      { question: 'Can I make the background white?', answer: 'Yes. Choose the white background option and Nexhance AI composites the transparent result onto white before delivery.' }
    ]
  },
  {
    slug: 'deblur',
    name: 'AI Deblur',
    shortName: 'Deblur',
    eyebrow: 'Unblur images and fix blurry photos',
    description: 'Unblur images affected by motion blur, soft focus and camera shake.',
    longDescription:
      'Unblur images and deblur photos affected by camera shake, subject motion or soft focus. Unlike a basic sharpen filter, the workflow is designed to recover edge structure and local definition while preserving the original image dimensions.',
    seoTitle: 'Unblur Image Online - Fix Blurry Photos with AI',
    seoDescription: 'Unblur images and fix blurry photos online with AI. Reduce camera shake, motion blur and soft focus while preserving natural texture.',
    searchTerms: ['deblur image', 'fix blurry photo', 'unblur image', 'remove motion blur', 'AI deblur'],
    icon: Focus,
    creditCost: 5,
    processingTime: '12–30 sec',
    formats: ['JPG', 'PNG', 'WebP', 'AVIF'],
    recommendedFor: ['Camera shake', 'Soft focus', 'Moving subjects'],
    options: [
      { id: 'preserveGrain', label: 'Preserve natural grain', type: 'toggle', defaultValue: true }
    ],
    benefits: [
      { title: 'Blur-specific restoration', description: 'Targets smeared and soft edge structure rather than only increasing contrast.' },
      { title: 'Natural texture option', description: 'Preserve grain to avoid a waxy result in photographs and film scans.' },
      { title: 'Original-size correction', description: 'Repair blur first, then upscale only when larger dimensions are required.' }
    ],
    howItWorks: [
      { title: 'Identify the blur type', description: 'Directional smear suggests motion; general softness may come from focus or resizing.' },
      { title: 'Run deblur before upscale', description: 'Correct the edge structure before adding more pixels.' },
      { title: 'Check high-contrast areas', description: 'Look for halos around faces, text, branches and bright objects.' }
    ],
    tips: [
      'Deblur cannot recover information that is fully smeared or hidden.',
      'Preserve grain for portraits and film-like photographs.',
      'Use Denoise first when high-ISO grain is stronger than the blur.',
      'Finish with only a light sharpen if the corrected image still needs crispness.'
    ],
    faqs: [
      { question: 'Is deblur the same as sharpen?', answer: 'No. Sharpening increases edge contrast. Deblur attempts to restore edge structure that has been spread by motion or focus softness.' },
      { question: 'Can it fix severe motion blur?', answer: 'It may improve visible structure, but strong motion can permanently hide detail. Results are best when the subject remains recognizable.' },
      { question: 'Should I preserve grain?', answer: 'Usually yes for natural photos. Turn it off only when the grain itself is distracting and you prefer a smoother output.' }
    ]
  },
  {
    slug: 'denoise',
    name: 'AI Denoise',
    shortName: 'Denoise',
    eyebrow: 'Remove image noise',
    description: 'Remove sensor noise, compression blocks and distracting grain.',
    longDescription:
      'Denoise low-light photos, high-ISO captures and compressed images without flattening every edge. Choose a light, balanced or strong cleanup level based on the amount of visible grain and artifacting.',
    seoTitle: 'AI Image Denoiser Online - Remove Noise and Grain',
    seoDescription: 'Remove image noise, high-ISO grain and compression artifacts online while protecting useful edges and natural texture.',
    searchTerms: ['denoise image', 'remove image noise', 'remove grain from photo', 'high ISO noise reduction', 'photo denoiser'],
    icon: Waves,
    creditCost: 5,
    processingTime: '12–30 sec',
    formats: ['JPG', 'PNG', 'WebP', 'AVIF'],
    recommendedFor: ['Night photos', 'High ISO', 'Compressed images'],
    options: [
      {
        id: 'strength',
        label: 'Noise reduction',
        type: 'select',
        defaultValue: 'balanced',
        choices: [
          { label: 'Light', value: 'light' },
          { label: 'Balanced', value: 'balanced' },
          { label: 'Strong', value: 'strong' }
        ]
      }
    ],
    benefits: [
      { title: 'Low-light cleanup', description: 'Reduces brightness and color noise that often appears in dark mobile and camera images.' },
      { title: 'Compression repair', description: 'Helps reduce visible blocks and edge artifacts in repeatedly saved files.' },
      { title: 'Strength control', description: 'Use the smallest cleanup level that removes distraction while retaining texture.' }
    ],
    howItWorks: [
      { title: 'Inspect the shadows', description: 'Noise is easiest to see in dark, flat areas and around compressed edges.' },
      { title: 'Choose a cleanup level', description: 'Light protects texture; Balanced handles common noise; Strong is for heavily degraded images.' },
      { title: 'Review texture', description: 'Check skin, hair, fabric, foliage and fine text for unwanted smoothing.' }
    ],
    tips: [
      'Denoise before upscaling so noise is not enlarged.',
      'Use Light for film grain and detailed portraits.',
      'Use Strong only when the source is visibly degraded.',
      'Apply sharpening after denoise, not before.'
    ],
    faqs: [
      { question: 'What causes image noise?', answer: 'Common causes include high ISO, small camera sensors, underexposure, heavy editing and repeated lossy compression.' },
      { question: 'Will denoise remove all grain?', answer: 'Strong settings can remove more grain, but complete smoothing may erase real texture. A balanced result usually looks more natural.' },
      { question: 'Can it fix JPEG blocks?', answer: 'It can reduce some compression artifacts, although severely damaged text and edges may not be fully recoverable.' }
    ]
  },
  {
    slug: 'sharpen',
    name: 'Smart Image Sharpen',
    shortName: 'Sharpen',
    eyebrow: 'Sharpen images online',
    description: 'Add crispness to web graphics, screenshots and finished photos.',
    longDescription:
      'Sharpen images online with a low-cost finishing pass for files that are already clean but need clearer edge definition. It is best used after resizing, denoise or compression, not as a substitute for true deblur restoration.',
    seoTitle: 'Image Sharpener Online - Make Photos Clearer',
    seoDescription: 'Sharpen images and make mildly soft photos clearer online with adjustable strength for pictures, screenshots and web graphics.',
    searchTerms: ['sharpen image', 'make image sharper', 'photo sharpen online', 'increase image clarity', 'sharpen screenshot'],
    icon: Aperture,
    creditCost: 1,
    processingTime: '2–6 sec',
    formats: ['JPG', 'PNG', 'WebP', 'AVIF'],
    recommendedFor: ['Web images', 'Screenshots', 'Final exports'],
    options: [
      {
        id: 'amount',
        label: 'Sharpen amount',
        type: 'range',
        defaultValue: 0.6,
        min: 0.1,
        max: 1,
        step: 0.1
      }
    ],
    benefits: [
      { title: 'Fast final polish', description: 'Adds definition after resizing, compression or gentle noise reduction.' },
      { title: 'Adjustable strength', description: 'Control the amount to avoid halos and harsh high-contrast edges.' },
      { title: 'Low-credit workflow', description: 'Useful for finishing many already-clean web and design assets.' }
    ],
    howItWorks: [
      { title: 'Start with a clean image', description: 'Remove strong noise or blur with the matching specialist tool first.' },
      { title: 'Increase gradually', description: 'Use a moderate value and judge the image at its actual display size.' },
      { title: 'Check edge halos', description: 'Inspect text, hair, architecture and bright outlines before download.' }
    ],
    tips: [
      'Sharpen after resizing for the final output dimensions.',
      'Avoid maximum strength on portraits and compressed images.',
      'Use Deblur when the problem is camera shake or motion smear.',
      'Judge the result at the size viewers will actually see.'
    ],
    faqs: [
      { question: 'Can sharpening fix a blurry photo?', answer: 'It can make mildly soft edges appear crisper, but it cannot reverse strong motion blur or focus errors. Use AI Deblur for those problems.' },
      { question: 'Why do bright outlines appear?', answer: 'Those are sharpening halos caused by too much edge contrast. Reduce the amount until the outline disappears.' },
      { question: 'When should I sharpen?', answer: 'Use it near the end of the workflow, after denoise, resize and compression decisions are complete.' }
    ]
  },
  {
    slug: 'relight',
    name: 'AI Photo Relighting',
    shortName: 'Relight',
    eyebrow: 'Relight photos with AI',
    description: 'Correct flat, harsh or badly directed light with a simple description.',
    longDescription:
      'Relight portraits, products and campaign images by describing the direction, softness and mood you want. The workflow changes the lighting treatment while trying to preserve the subject, framing and core composition.',
    seoTitle: 'AI Photo Relighting Online - Change Image Lighting',
    seoDescription: 'Relight photos online with AI. Improve flat, harsh or poorly directed light for portraits, products and campaign images.',
    searchTerms: ['AI relight photo', 'change photo lighting', 'relight image', 'fix bad lighting photo', 'studio lighting AI'],
    icon: Lightbulb,
    creditCost: 6,
    processingTime: '20–50 sec',
    formats: ['JPG', 'PNG', 'WebP'],
    recommendedFor: ['Portraits', 'Products', 'Campaign images'],
    options: [
      {
        id: 'prompt',
        label: 'Lighting direction',
        type: 'text',
        defaultValue: 'soft professional studio light from the front-left, natural shadows',
        help: 'Describe direction, softness, color temperature or mood.'
      }
    ],
    benefits: [
      { title: 'Prompt-based control', description: 'Describe direction, softness, temperature and atmosphere in ordinary language.' },
      { title: 'Subject-preserving workflow', description: 'Designed to change lighting while retaining the main subject and composition.' },
      { title: 'Creative and corrective use', description: 'Improve flat images or explore a more deliberate studio and campaign mood.' }
    ],
    howItWorks: [
      { title: 'Choose a clear subject', description: 'Images with readable depth and visible facial or product structure give the model better guidance.' },
      { title: 'Describe the light', description: 'Include direction, softness, color temperature and mood rather than vague words alone.' },
      { title: 'Check shadows and materials', description: 'Review skin, reflective surfaces, transparent objects and contact shadows for realism.' }
    ],
    tips: [
      'Use descriptions such as “soft front-left studio light” instead of only “better lighting”.',
      'Match the new light direction to visible shadows and scene depth.',
      'Verify product color after warm, neon or dramatic lighting changes.',
      'Do not use relighting to misrepresent product condition or appearance.'
    ],
    faqs: [
      { question: 'What should I write in the lighting prompt?', answer: 'Describe direction, softness, temperature and mood, for example, “soft neutral studio light from the front-left with natural shadows”.' },
      { question: 'Will relighting change the background?', answer: 'It may alter background brightness and color because light affects the whole scene. Compare the composition carefully.' },
      { question: 'Can it fix an underexposed photo?', answer: 'It can improve perceived lighting, but severely clipped shadows or highlights may contain no recoverable source detail.' }
    ]
  },
  {
    slug: 'product-photo',
    name: 'Product Photo Enhancer',
    shortName: 'Product photo',
    eyebrow: 'Enhance product photos',
    description: 'Improve product detail, lighting and presentation for online stores.',
    longDescription:
      'Enhance product photos for online stores, catalogs and social campaigns. The workflow improves edge definition, material texture and visual separation while keeping product accuracy as the main quality check.',
    seoTitle: 'AI Product Photo Enhancer for E-commerce Images',
    seoDescription: 'Enhance product photos for Shopify, marketplaces and campaigns with clearer detail, cleaner texture and stronger presentation.',
    searchTerms: ['product photo enhancer', 'ecommerce image enhancer', 'improve product image', 'Shopify product photo', 'AI product photography'],
    icon: WandSparkles,
    creditCost: 6,
    processingTime: '20–45 sec',
    formats: ['JPG', 'PNG', 'WebP'],
    recommendedFor: ['Online stores', 'Catalogs', 'Social campaigns'],
    options: [
      {
        id: 'style',
        label: 'Presentation style',
        type: 'select',
        defaultValue: 'natural',
        choices: [
          { label: 'Natural product detail', value: 'natural' },
          { label: 'Clean marketplace', value: 'marketplace' },
          { label: 'Premium campaign', value: 'premium' }
        ]
      }
    ],
    benefits: [
      { title: 'Clearer material detail', description: 'Improves texture and edge visibility for products that look soft or compressed online.' },
      { title: 'Three presentation intents', description: 'Choose natural detail, clean marketplace output or a premium campaign treatment.' },
      { title: 'Designed for store workflows', description: 'Useful for product pages, catalogs, social campaigns and marketplace zoom.' }
    ],
    howItWorks: [
      { title: 'Upload an accurate source', description: 'Use even lighting and a clear, unobstructed view of the real product.' },
      { title: 'Choose the presentation', description: 'Select the level that matches a factual listing or a more creative campaign image.' },
      { title: 'Verify product accuracy', description: 'Compare color, shape, labels, texture and included details before publishing.' }
    ],
    tips: [
      'Keep primary listing images free from promotional overlays and watermarks.',
      'Use consistent crop, background and lighting across the catalog.',
      'Reject any result that changes text, material, product shape or color.',
      'Use Background Remover before enhancement when the source is visually busy.'
    ],
    faqs: [
      { question: 'Can I use the result for Shopify or marketplaces?', answer: 'The workflow is designed for e-commerce images, but you must follow the current requirements of the destination platform and verify that the result accurately represents the product.' },
      { question: 'Will the enhancer change product text or labels?', answer: 'Generative detail can alter very small text or patterns. Inspect labels, packaging, logos and serial information before publishing.' },
      { question: 'Which style should I choose?', answer: 'Use Natural for factual product pages, Marketplace for clean listing presentation and Premium Campaign for creative promotional assets that will receive closer review.' }
    ]
  }
];

export const tools: ToolDefinition[] = [...baseTools, ...(expandedTools as unknown as ToolDefinition[])].map((tool) => ({
  ...tool,
  longDescription: toolLongDescriptions[tool.slug] ?? tool.longDescription,
  mode: tool.mode ?? 'ai',
  category: tool.category ?? 'Enhance & Repair',
  faqs: [...tool.faqs, ...(toolFaqSupplements[tool.slug] ?? [])]
}));

export const aiTools = tools.filter((tool) => tool.mode !== 'free');
export const freeTools = tools.filter((tool) => tool.mode === 'free');

export const toolMap = Object.fromEntries(tools.map((tool) => [tool.slug, tool])) as Record<ToolSlug, ToolDefinition>;

export function estimateCredits(
  slug: ToolSlug,
  options: Record<string, string | number | boolean> = {},
  megapixels = 1
) {
  return estimateJobCredits(slug, options, megapixels);
}
